import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FaRegTrashAlt, FaRegEdit } from "react-icons/fa";
import { MdAdd, MdClose, MdCheck } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import { FiImage, FiTag } from "react-icons/fi";
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import PanelSkeleton from "../PanelSkeleton";
import ProductImage from "../ProductImage";
import mediaUrl from "../../utils/mediaUrl";
import store from "../../redux/store";
import styles from "../../styles/categoriesPanel.module.css";

const authConfig = (extra = {}) => {
  const token = store.getState().auth?.token;
  return {
    withCredentials: true,
    ...extra,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra.headers },
  };
};

const DEFAULT_FORM = { name: "" };

const CategoriesPanel = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [reordering, setReordering] = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState(DEFAULT_FORM);
  const [imageFile, setImageFile]   = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImg, setRemoveImg]   = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const nameRef  = useRef(null);
  const imgRef   = useRef(null);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/categories/dashboardFetch`,
        authConfig()
      );
      setCategories(res.data?.data ?? []);
    } catch {
      toast.error(<span className="toast">فشل تحميل التصنيفات</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditId(null);
    setForm(DEFAULT_FORM);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImg(false);
    setShowForm(true);
    setTimeout(() => nameRef.current?.focus(), 80);
  };

  const openEdit = (cat) => {
    setEditId(cat.id);
    setForm({ name: cat.name });
    setImageFile(null);
    setImagePreview(cat.imageUrl ? mediaUrl(cat.imageUrl) : null);
    setRemoveImg(false);
    setShowForm(true);
    setTimeout(() => nameRef.current?.focus(), 80);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    setForm(DEFAULT_FORM);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImg(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      toast.error(<span className="toast">حجم الصورة يجب أن يكون أقل من 3 ميجابايت</span>);
      e.target.value = "";
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setRemoveImg(false);
    e.target.value = "";
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImg(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      if (imageFile) fd.append("imageFile", imageFile);

      if (editId) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/categories/${editId}`,
          fd,
          authConfig()
        );
        if (removeImg && !imageFile) {
          await axios.delete(
            `${process.env.REACT_APP_BACKEND_URL}/api/categories/${editId}/image`,
            authConfig()
          );
        }
        toast.success(<span className="toast">تم تعديل التصنيف</span>);
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/categories`,
          fd,
          authConfig()
        );
        toast.success(<span className="toast">تم إضافة التصنيف</span>);
      }
      closeForm();
      fetchCategories();
    } catch {
      toast.error(<span className="toast">فشلت العملية. حاول مرة أخرى</span>);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (cat) => {
    confirmAlert({
      message: `حذف تصنيف "${cat.name}" ؟`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setDeleting(true);
            try {
              await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/categories/${cat.id}`,
                authConfig()
              );
              toast.success(<span className="toast">تم حذف التصنيف</span>);
              fetchCategories();
            } catch {
              toast.error(<span className="toast">فشل الحذف. حاول مرة أخرى</span>);
            } finally {
              setDeleting(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  const move = async (index, direction) => {
    const newCats = [...categories];
    const swapIdx = direction === "up" ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newCats.length) return;

    [newCats[index], newCats[swapIdx]] = [newCats[swapIdx], newCats[index]];
    setCategories(newCats);

    setReordering(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/categories/reorder/batch`,
        {
          items: newCats.map((cat, i) => ({ id: cat.id, sortOrder: i })),
        },
        authConfig()
      );
    } catch {
      toast.error(<span className="toast">فشل حفظ الترتيب. حاول مرة أخرى</span>);
      fetchCategories();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className={styles.panel}>

      {/* ── Header ── */}
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>
          <FiTag className={styles.panelTitleIcon} />
          التصنيفات
        </span>
        <button className={styles.addBtn} onClick={openAdd} disabled={showForm}>
          <MdAdd /> إضافة تصنيف
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showForm && (
        <form className={styles.form} onSubmit={handleSubmit} encType="multipart/form-data">
          <div className={styles.formHead}>
            <span className={styles.formTitle}>{editId ? "تعديل التصنيف" : "تصنيف جديد"}</span>
            <button type="button" className={styles.formClose} onClick={closeForm}>
              <MdClose />
            </button>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>اسم التصنيف *</label>
            <input
              ref={nameRef}
              className={styles.formInput}
              placeholder="مثال: غرفة نوم"
              maxLength={100}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>صورة التصنيف (اختياري)</label>
            {imagePreview ? (
              <div className={styles.imgPreviewWrap}>
                <ProductImage
                  url={imagePreview}
                  alt="preview"
                  className={styles.imgPreview}
                />
                <button
                  type="button"
                  className={styles.imgRemoveBtn}
                  onClick={handleRemoveImage}
                  title="إزالة الصورة"
                >
                  <MdClose />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={styles.imgPickBtn}
                onClick={() => imgRef.current?.click()}
              >
                <FiImage className={styles.imgPickIcon} />
                اختيار صورة
              </button>
            )}
            <input
              type="file"
              accept="image/*"
              ref={imgRef}
              onChange={handleImageChange}
              style={{ display: "none" }}
            />
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? <ImSpinner2 className="smallspinner" /> : <><MdCheck /> حفظ</>}
            </button>
            <button type="button" className={styles.cancelFormBtn} onClick={closeForm}>
              إلغاء
            </button>
          </div>
        </form>
      )}

      {/* ── Categories List ── */}
      {loading ? (
        <PanelSkeleton count={3} />
      ) : categories.length === 0 ? (
        <p className="warn">لا توجد تصنيفات بعد. أضف أول تصنيف!</p>
      ) : (
        <div className={styles.list}>
          {categories.map((cat, i) => (
            <div key={cat.id} className={styles.row} style={{ animationDelay: `${i * 0.04}s` }}>

              {/* Order controls */}
              <div className={styles.orderBtns}>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "up")}
                  disabled={i === 0 || reordering || deleting}
                  title="تحريك لأعلى"
                >
                  <MdKeyboardArrowUp />
                </button>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "down")}
                  disabled={i === categories.length - 1 || reordering || deleting}
                  title="تحريك لأسفل"
                >
                  <MdKeyboardArrowDown />
                </button>
              </div>

              {/* Thumbnail */}
              <div className={styles.thumb}>
                {cat.imageUrl ? (
                  <ProductImage
                    url={mediaUrl(cat.imageUrl)}
                    alt={cat.name}
                    className={styles.thumbImg}
                  />
                ) : (
                  <div className={styles.thumbPlaceholder}>
                    <FiImage />
                  </div>
                )}
              </div>

              {/* Name */}
              <span className={styles.catName}>{cat.name}</span>

              {/* Order badge */}
              <span className={styles.orderBadge}>{i + 1}</span>

              {/* Actions */}
              <div className={styles.actions}>
                <button
                  className={styles.editBtn}
                  onClick={() => openEdit(cat)}
                  disabled={deleting}
                  title="تعديل"
                >
                  <FaRegEdit />
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => !deleting && handleDelete(cat)}
                  disabled={deleting}
                  title="حذف"
                >
                  {deleting ? <ImSpinner2 className="smallspinner" /> : <FaRegTrashAlt />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className={styles.hint}>
        ترتيب التصنيفات هنا يتحكم في ترتيب ظهورها في الصفحة الرئيسية. استخدم أسهم الترتيب لتغيير الترتيب.
      </p>
    </div>
  );
};

export default CategoriesPanel;
